/**
 * Fix #2 Step 2: single source of truth for place-detail URLs.
 *
 * Previously ~16 files across the codebase inlined `/park/${id}` string
 * concatenation. When the URL scheme moved from `/park/{place_id}` to
 * `/{category-slug}/{city-slug}/{place-slug}` this meant every one of
 * those sites had to be grep-and-fixed. This helper is the fix — all
 * inbound links now flow through `placeUrl(park)` and future URL
 * changes are a one-file edit.
 *
 * Precedence:
 *   1. Community places (source="community") → /place/{slug}  (unchanged)
 *   2. Manual service listings (source="manual_add") — use the same
 *      /park/{id} scheme as before (they don't carry canonical city
 *      hubs; their URLs stay opaque). No canonical URL rewrite.
 *   3. Google-sourced places with slug + place_type + city + state →
 *      /{cat-url-slug}/{city-slug}/{slug}
 *   4. Fallback (missing slug or city — pre-Fix #2 backfill or an
 *      unusual doc) → /park/{id}
 *
 * The fallback path is intentionally forgiving: it's safer to serve a
 * legacy URL than to break a link builder. The backfill run should
 * eliminate step 4 for the entire Google-sourced corpus, so any place
 * still falling through in production is a genuine edge case.
 */
import { CATEGORIES } from "./categories";

const CATEGORY_URL_SLUG_BY_KEY = Object.fromEntries(
  CATEGORIES.map((c) => [c.key, c.urlSlug])
);

// Same kebab rules as `lib/cities.js:kebab()` — kept inline here so
// this helper has zero cross-lib dependencies at import time.
const kebabCity = (s) => {
  if (!s) return "";
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[''.]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const buildCitySlug = (city, state) => {
  const c = kebabCity(city);
  const st = (state || "").toLowerCase();
  if (!c || !st) return "";
  return `${c}-${st}`;
};

/**
 * Return the canonical detail URL for a park/place doc.
 * Never returns an empty string — falls back to /park/{id} when
 * canonical fields are missing.
 */
export const placeUrl = (park) => {
  if (!park) return "/search";
  const src = park.source || "google_places_legacy";

  // 1. Community places → /place/{slug}
  if (src === "community" && park.slug) {
    return `/place/${park.slug}`;
  }

  // 2. Google-sourced AND manual-add both route through the canonical
  // /{catSlug}/{citySlug}/{slug} scheme when they have a slug + city
  // + state. Manual listings got a slug backfill on 9/6/2026 (see
  // backend/scripts/seed_manual_places.py::seed_all); before that
  // they were force-routed to /park/{id} here, which meant crawlers
  // hit the SPA shell verbatim and picked up the homepage's canonical
  // and OG tags. Removing the manual_add special-case unblocks proper
  // SEO metadata for those pages.
  const catSlug = CATEGORY_URL_SLUG_BY_KEY[park.place_type];
  const citySlug = buildCitySlug(park.city, park.state);
  if (catSlug && citySlug && park.slug) {
    return `/${catSlug}/${citySlug}/${park.slug}`;
  }

  // 3. Fallback — legacy /park/{id}. Still resolves in App.js for
  // docs that lack a slug (shouldn't happen for manual_add post-9/6
  // but the fallback keeps old shared links working).
  return `/park/${park.id}`;
};

/** Absolute variant for shareable links / JSON-LD emit. */
export const absolutePlaceUrl = (park, baseUrl) => {
  const path = placeUrl(park);
  const origin = (baseUrl || "").replace(/\/$/, "");
  return `${origin}${path}`;
};
